export const VALUE_SOURCE_QUESTION = 'question';
export const VALUE_SOURCE_DEFAULT = 'default';

export function normalizeValueSource(value) {
  return value === VALUE_SOURCE_DEFAULT ? VALUE_SOURCE_DEFAULT : VALUE_SOURCE_QUESTION;
}

export function toDynamicFieldConfig(field) {
  if (!field) return null;
  const dataType = String(field.data_type || field.dataType || 'text').toLowerCase();
  return {
    ...field,
    field_name: field.field_name || field.fieldName,
    data_type: dataType,
    reference_object: field.reference_object || field.referenceObject,
    lookup_search_fields: field.lookup_search_fields || field.lookupSearchFields,
    display_fields: field.display_fields || field.displayFields,
    lookup_filter: field.lookup_filter ?? field.lookupFilter,
    is_reference:
      field.is_reference === true ||
      field.isReference === true ||
      dataType === 'lookup',
  };
}

export function getLeadFieldMeta(leadFields, fieldName) {
  const name = String(fieldName || '').toLowerCase();
  return (leadFields || []).find(
    (field) =>
      String(field.field_name || field.fieldName || '').toLowerCase() === name
  );
}

/** Matches EditLayout / object model: field or layout required flag. */
export function isLeadFieldRequired(field) {
  if (!field) return false;
  return !!(
    field.isRequired ||
    field.is_required ||
    field.is_required_layout
  );
}

export function resolveMappingRowRequired(row, leadFields) {
  return isLeadFieldRequired(getLeadFieldMeta(leadFields, row.leadField));
}

export function isLookupField(field) {
  if (!field) return false;
  const dataType = String(field.data_type || field.dataType || '').toLowerCase();
  return (
    dataType === 'lookup' ||
    field.is_reference === true ||
    field.isReference === true
  );
}

export function formatDefaultValueLabel(row, leadFields) {
  const display = row.defaultDisplayValue != null ? String(row.defaultDisplayValue).trim() : '';
  if (display) return display;

  const value = row.defaultValue != null ? String(row.defaultValue).trim() : '';
  if (!value) return '—';

  const fieldMeta = getLeadFieldMeta(leadFields, row.leadField);
  if (isLookupField(fieldMeta)) {
    return value;
  }

  return value;
}

export const DEFAULT_LEAD_FIELD_KEYS = [
  { keys: ['firstname', 'first_name'], label: 'First Name' },
  { keys: ['lastname', 'last_name'], label: 'Last Name' },
  {
    keys: ['whatsapp_number', 'phone', 'mobile', 'phone_number'],
    label: 'WhatsApp Number',
  },
];

export function findLeadFieldByKeys(leadFields, keyCandidates) {
  const normalized = (keyCandidates || []).map((k) => String(k).toLowerCase());
  return (leadFields || []).find((field) =>
    normalized.includes(String(field.field_name || field.fieldName || '').toLowerCase())
  );
}

export function buildDefaultMappingRows(leadFields) {
  const used = new Set();
  const rows = [];

  DEFAULT_LEAD_FIELD_KEYS.forEach((def) => {
    const match = findLeadFieldByKeys(
      leadFields.filter((f) => !used.has(String(f.field_name || f.fieldName))),
      def.keys
    );
    if (!match) return;
    const leadField = match.field_name || match.fieldName;
    used.add(String(leadField));
    rows.push({
      id: `default-${leadField}`,
      leadField,
      leadFieldLabel: match.field_label || match.fieldLabel || def.label,
      questionKey: '',
      questionLabel: '',
      questionId: '',
      questionType: '',
      required: isLeadFieldRequired(match),
      isDefault: true,
      isUnique: false,
      valueSource: VALUE_SOURCE_QUESTION,
      defaultValue: '',
      defaultDisplayValue: '',
    });
  });

  return rows;
}

export function parseStoredMapping(fieldMapping, leadFields) {
  if (!fieldMapping) {
    return buildDefaultMappingRows(leadFields);
  }

  if (Array.isArray(fieldMapping.rows)) {
    return fieldMapping.rows.map((row, index) => ({
      id: row.id || `row-${index}-${row.leadField}`,
      leadField: row.leadField || '',
      leadFieldLabel: row.leadFieldLabel || row.leadField || '',
      questionKey: row.questionKey || '',
      questionLabel: row.questionLabel || '',
      questionId: row.questionId || '',
      questionType: row.questionType || '',
      required: resolveMappingRowRequired(row, leadFields),
      isDefault: row.isDefault !== false,
      isUnique: !!row.isUnique,
      valueSource: normalizeValueSource(row.valueSource),
      defaultValue: row.defaultValue != null ? String(row.defaultValue) : '',
      defaultDisplayValue:
        row.defaultDisplayValue != null ? String(row.defaultDisplayValue) : '',
    }));
  }

  return buildDefaultMappingRows(leadFields);
}

export function buildMappingPayload(rows) {
  const uniqueCount = (rows || []).filter((row) => row.isUnique).length;
  if (uniqueCount > 1) {
    throw new Error('Only one CRM lead field can be marked as unique.');
  }

  for (const row of rows || []) {
    const source = normalizeValueSource(row.valueSource);
    const defaultValue =
      row.defaultValue != null ? String(row.defaultValue).trim() : '';
    const hasQuestion = !!row.questionKey;

    if (source === VALUE_SOURCE_DEFAULT) {
      if (!defaultValue) {
        throw new Error(
          `${row.leadFieldLabel || row.leadField} requires a default value.`
        );
      }
      continue;
    }

    if (row.required && !hasQuestion) {
      throw new Error(
        `${row.leadFieldLabel || row.leadField} requires a Facebook question mapping.`
      );
    }
  }

  return {
    version: 1,
    rows: (rows || []).map((row) => ({
      leadField: row.leadField,
      leadFieldLabel: row.leadFieldLabel,
      questionKey: row.questionKey || null,
      questionLabel: row.questionLabel || null,
      questionId: row.questionId || null,
      questionType: row.questionType || null,
      required: !!row.required,
      isDefault: !!row.isDefault,
      isUnique: !!row.isUnique,
      valueSource: normalizeValueSource(row.valueSource),
      defaultValue:
        row.defaultValue != null && String(row.defaultValue).trim() !== ''
          ? String(row.defaultValue).trim()
          : null,
      defaultDisplayValue:
        row.defaultDisplayValue != null &&
        String(row.defaultDisplayValue).trim() !== ''
          ? String(row.defaultDisplayValue).trim()
          : null,
    })),
  };
}

export function formatQuestionOption(question) {
  const label = question?.label || question?.key || 'Question';
  const key = question?.key ? ` (${question.key})` : '';
  return `${label}${key}`;
}

export function formatMappingSummary(fieldMapping) {
  if (!fieldMapping) return '—';
  const rows = Array.isArray(fieldMapping.rows) ? fieldMapping.rows : [];
  if (rows.length === 0) return '—';

  const describeRow = (row) => {
    const label = row.leadFieldLabel || row.leadField;
    if (normalizeValueSource(row.valueSource) === VALUE_SOURCE_DEFAULT && row.defaultValue) {
      const display =
        row.defaultDisplayValue != null && String(row.defaultDisplayValue).trim() !== ''
          ? String(row.defaultDisplayValue).trim()
          : String(row.defaultValue);
      return `${label} = ${display}`;
    }
    if (row.questionKey) {
      return `${label} → ${row.questionKey}`;
    }
    return null;
  };

  const mapped = rows.map(describeRow).filter(Boolean);
  if (mapped.length === 0) return '—';
  if (mapped.length <= 2) {
    return mapped.join(', ');
  }
  return `${mapped.length} field(s) mapped`;
}
